import { ElementRef } from '@angular/core';
import { Selection } from 'd3';
import * as d3 from 'd3';
import { Application, Container } from 'pixi.js';
import { Subject } from 'rxjs';
import { EnclosureDisk, VDevMetadata } from 'app/core/classes/system-profiler';
import { CoreEvent } from 'app/interfaces/events';
import { Theme } from 'app/services/theme/theme.service';
import { ChassisView } from './chassis-view';

export class VDevLabelsSVG {
  /*
  * We create an SVG layer on top of the PIXI canvas
  * to achieve crisper lines. Apparently drawing
  * thin lines in WebGL is problematic without
  * resorting to caching them as bitmaps which
  * essentially renders them static.
  *
  */

  events$: Subject<CoreEvent>;

  protected svg: Selection<SVGSVGElement, unknown, HTMLElement, unknown>; // Our d3 generated svg layer
  protected mainStage: Container; // WebGL Canvas
  protected app: Application;
  protected chassis: ChassisView; // The chassis we are labelling
  color: string;
  selectedDiskColor: string;
  highlightColor: string;
  highlightedDiskName: string;
  selectedDisk: EnclosureDisk;

  private trays: Record<string, { x: number; y: number; width: number; height: number }> = {};

  constructor(chassis: ChassisView, app: Application, theme: Theme, disk: EnclosureDisk) {
    this.selectedDisk = disk;
    this.color = 'var(--cyan)';
    this.selectedDiskColor = 'var(--yellow)';
    this.highlightColor = theme.yellow;

    this.onInit(chassis, app);
  }

  onInit(chassis: ChassisView, app: Application): void {
    this.chassis = chassis;
    this.app = app;
    this.mainStage = this.app.stage;
    this.d3Init();

    let tiles;
    this.events$ = new Subject<CoreEvent>();
    this.events$.subscribe((evt: CoreEvent): void => {
      switch (evt.name) {
        case 'ThemeChanged':
          const theme = evt.data;
          this.color = theme.blue;
          this.selectedDiskColor = theme.cyan;
          this.highlightColor = theme.yellow;
          break;
        case 'LabelDrives':
          this.createVdevLabels(evt.data);
          break;
        case 'OverlayReady':
          break;
        case 'ShowPath':
          break;
        case 'HidePath':
          break;
        case 'EnableHighlightMode':
          break;
        case 'DisableHighlightMode':
          tiles = this.getParent().querySelectorAll('rect.tile');
          this.showAllTiles(tiles as NodeListOf<HTMLElement>);
          break;
        case 'HighlightDisk':
          tiles = this.getParent().querySelectorAll('rect.tile');
          this.hideAllTiles(tiles as NodeListOf<HTMLElement>);

          this.highlightedDiskName = evt.data.devname;
          this.showTile(evt.data.devname);
          break;
        case 'UnhighlightDisk':
          break;
      }
    });
  }

  // Animate into view
  enter(): void {
  }

  // Animate out of view
  exit(): void {
    const op = this.getParent();
    d3.select('#' + op.id + ' svg').remove();
    d3.select('#' + op.id + ' canvas.clickpad').remove();
    this.app.renderer.plugins.interaction.setTargetElement(this.app.renderer.view);
  }

  d3Init(): void {
    const op = this.getParent();

    this.svg = d3.select('#' + op.id).append('svg')
      .attr('width', op.offsetWidth)
      .attr('height', op.offsetHeight)
      .attr('style', 'position:absolute; top:0; left:0;');

    /* const clickpad = */
    d3.select('#' + op.id).append('canvas') // This element will capture pointer for PIXI
      .attr('class', 'clickpad')
      .attr('width', op.offsetWidth)
      .attr('height', op.offsetHeight)
      .attr('style', 'position:absolute; top:0; left:0;');

    this.app.renderer.plugins.interaction.setTargetElement(op.querySelector('canvas.clickpad'));
  }

  getParent(): HTMLElement {
    return this.app.renderer.view.offsetParent as HTMLElement;
  }

  createVdevLabelTile(
    x: number,
    y: number,
    w: number,
    h: number,
    className: string,
    diskName: string,
  ): void {
    const color = diskName == this.selectedDisk.devname ? this.selectedDiskColor : this.color;
    let opacity = diskName == this.selectedDisk.devname ? 1 : 0.5;
    opacity = 1;

    const style = 'fill-opacity:0.25; stroke-width:1';

    this.svg.append('rect')
      .attr('class', className)
      .attr('y', y)
      .attr('x', x)
      .attr('width', w)
      .attr('height', h)
      .attr('fill', color)
      .attr('stroke', color)
      .attr('stroke-opacity', opacity)
      .attr('style', style);
  }

  createVdevLabels(vdev: VDevMetadata): void {
    const disks = vdev.disks
      ? Object.keys(vdev.disks)
      : [this.selectedDisk.devname]; // NOTE: vdev.slots only has values for current enclosure

    disks.forEach((disk) => {
      const slot = typeof vdev.slots !== 'undefined' ? vdev.slots[disk] : this.selectedDisk.enclosure.slot;

      if (slot && slot >= this.chassis.slotRange.start && slot <= this.chassis.slotRange.end) {
        // Create tile if the disk is in the current enclosure
        const dt = this.chassis.driveTrayObjects.find((dto) => parseInt(dto.id) == slot);
        const src = dt.container;
        const tray = src.getGlobalPosition();

        const tileClass = 'tile tile_' + disk;

        const tileWidth = src.width * this.chassis.driveTrays.scale.x * this.chassis.container.scale.x;
        const tileHeight = src.height * this.chassis.driveTrays.scale.y * this.chassis.container.scale.y;

        this.createVdevLabelTile(tray.x, tray.y, tileWidth, tileHeight, tileClass, disk);
        this.trays[disk] = {
          x: tray.x, y: tray.y, width: tileWidth, height: tileHeight,
        };
      }
    });
  }

  calculateParentOffsets(el: ElementRef): { x: number; y: number } {
    // Template uses CSS to center and align text so
    // we need to compensate with absolute positions
    // of wrapper elements

    // 1 up
    const legend = el.nativeElement.childNodes[0].childNodes[1];

    // 2 up
    const content = el.nativeElement.childNodes[0];

    const xOffset = el.nativeElement.offsetLeft + legend.offsetLeft + content.offsetLeft;
    const yOffset = el.nativeElement.offsetTop + legend.offsetTop + content.offsetTop;

    return { x: xOffset, y: yOffset - 6 };
  }

  showTile(devname: string): void {
    const targetEl: HTMLElement = this.getParent().querySelector('rect.tile_' + devname);
    if (targetEl) {
      targetEl.style.opacity = '1';
    }
  }

  hideTile(devname: string): void {
    const targetEl: HTMLElement = this.getParent().querySelector('rect.tile_' + devname);
    if (targetEl) {
      targetEl.style.opacity = '0';
    }
  }

  hideAllTiles(tiles: NodeListOf<HTMLElement>): void {
    tiles.forEach((item) => {
      item.style.opacity = '0';
    });
  }

  showAllTiles(tiles: NodeListOf<HTMLElement>): void {
    tiles.forEach((item) => {
      item.style.opacity = '1';
    });
  }

  protected parseColor(color: string): number {
    return parseInt('0x' + color.substring(1), 16);
  }
}
